// ============================================================================
// useDashboard — one hook per widget so screens can fetch them in parallel.
// ============================================================================
// Loop-safety: the `fetcher` arg is captured in a ref so callers don't have to
// memoize it. Effect deps are [enabled, filter] only — `filter` MUST be a
// stable reference (use useMemo at the parent), otherwise this still re-fires.

import { useCallback, useEffect, useRef, useState } from 'react';
import { dashboardService } from 'api/services/dashboardService';
import type {
  DashboardFilter,
  BookingOverviewWidget,
  RevenueWidget,
  ApprovalWidget,
  ForwardOccupancyWidget,
  AgentCorporateWidget,
} from 'api/types';

interface WidgetState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

function useWidget<T>(
  fetcher: (filter: DashboardFilter) => Promise<{ data?: T | null }>,
  filter: DashboardFilter,
  enabled: boolean,
): WidgetState<T> {
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const [data, setData]       = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<Error | null>(null);

  // ── Initial / dependency-driven fetch ────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetcherRef.current(filter)
      .then((r) => { if (!cancelled) setData(r?.data ?? null); })
      .catch((e: Error) => { if (!cancelled) { setError(e); setData(null); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [enabled, filter]);

  // ── Manual refetch (e.g. Refresh button) ─────────────────────────────────
  const refetch = useCallback(() => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    fetcherRef.current(filter)
      .then((r) => setData(r?.data ?? null))
      .catch((e: Error) => { setError(e); setData(null); })
      .finally(() => setLoading(false));
  }, [enabled, filter]);

  return { data, loading, error, refetch };
}

export const useBookingOverviewWidget = (filter: DashboardFilter, enabled = true) =>
  useWidget<BookingOverviewWidget>((f) => dashboardService.getBookingOverview(f), filter, enabled);

export const useRevenueWidget = (filter: DashboardFilter, enabled = true) =>
  useWidget<RevenueWidget>((f) => dashboardService.getRevenue(f), filter, enabled);

export const useApprovalWidget = (filter: DashboardFilter, enabled = true) =>
  useWidget<ApprovalWidget>((f) => dashboardService.getApproval(f), filter, enabled);

export const useForwardOccupancyWidget = (filter: DashboardFilter, enabled = true) =>
  useWidget<ForwardOccupancyWidget>((f) => dashboardService.getForwardOccupancy(f), filter, enabled);

export const useAgentCorporateWidget = (filter: DashboardFilter, enabled = true) =>
  useWidget<AgentCorporateWidget>((f) => dashboardService.getAgentCorporate(f), filter, enabled);
